import React from 'react'
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client'
import { useMsal, useAccount } from '@azure/msal-react'
import { InteractionStatus, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { CachePersistor } from 'apollo3-cache-persist'
import { AuthContext } from './AuthContext'
import { onNotLogged, onTokenExpired, onUnexpected } from './commons'
import { UserPreferencesProvider } from '../preferences/UserPreferencesProvider'
import { getClient, SessionHandlers } from '../../graphql/client'
import { User } from '../../userprofile/types/user'
import {
  createSessionExpiredHandler,
  transformAzureUserToUser,
  validateAzureUser,
  matchAuthenticationErrors,
} from './commons'
import { createTokenProvider } from './token-provider'
import { LoadingCircular } from '../../components/LoadingCircular'
import { useLDClient } from 'launchdarkly-react-client-sdk'

export const SESSION_EXPIRY_REFRESH_FLAG = 'session-expiry-refresh'

const AuthProviderAzure: React.FC = ({ children }) => {
  const ldClient = useLDClient()
  // The Apollo client is built before LaunchDarkly identifies the user, so the flag is read per
  // operation through this ref instead of once at construction.
  const ldClientRef = React.useRef(ldClient)
  ldClientRef.current = ldClient
  const [session, setSession] = React.useState<SessionHandlers>()
  const [user, setUser] = React.useState<User | null>(null)
  const [graphqlClient, setGraphqlClient] = React.useState<ApolloClient<NormalizedCacheObject> | null>(null)
  const [persistor, setPersistor] = React.useState<CachePersistor<NormalizedCacheObject> | null>(null)
  const [isLdReady, setIsLdReady] = React.useState(false)
  const { instance: AzureInstance, accounts, inProgress } = useMsal()

  const account = useAccount(accounts[0] || {})

  React.useEffect(() => {
    if (account && inProgress === InteractionStatus.None) {
      const silentRequest: SilentRequest = {
        scopes: ['openid', 'profile', 'offline_access'],
        account: account,
      }
      const tokenProvider = createTokenProvider(AzureInstance, silentRequest.scopes)
      const sessionHandlers: SessionHandlers = {
        isEnabled: () => ldClientRef.current?.variation(SESSION_EXPIRY_REFRESH_FLAG, false) === true,
        getToken: tokenProvider.getToken,
        onSessionExpired: createSessionExpiredHandler(() => AzureInstance.loginRedirect(silentRequest)),
      }
      AzureInstance.acquireTokenSilent(silentRequest)
        .then((response) => {
          const idTokenClaims = {
            ...response.idTokenClaims,
            idToken: response.idToken,
          }
          validateAzureUser(idTokenClaims)
            .then(transformAzureUserToUser)
            .then(async (validatedUser) => {
              setUser(validatedUser)
              const { client, persistor } = await getClient(validatedUser.token, sessionHandlers)
              setSession(sessionHandlers)
              setGraphqlClient(client)
              setPersistor(persistor)
            })
            .catch(
              matchAuthenticationErrors({
                noLogged: onNotLogged(() => AzureInstance.loginRedirect(silentRequest)),
                tokenExpired: onTokenExpired(() => AzureInstance.logoutRedirect()),
                unexpected: onUnexpected(() => AzureInstance.logoutRedirect()),
              }),
            )
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            AzureInstance.acquireTokenRedirect(silentRequest)
          } else {
            matchAuthenticationErrors({
              noLogged: onNotLogged(() => AzureInstance.loginRedirect(silentRequest)),
              tokenExpired: onTokenExpired(() => AzureInstance.logoutRedirect()),
              unexpected: onUnexpected(() => AzureInstance.logoutRedirect()),
            })(error)
          }
        })
    } else if (accounts.length === 0 && inProgress === InteractionStatus.None) {
      AzureInstance.loginRedirect()
    }
  }, [account, accounts.length, inProgress, AzureInstance])

  React.useEffect(() => {
    if (user && ldClient) {
      ldClient
        .identify({
          kind: 'user',
          key: user.email,
          email: user.email,
        })
        .then(() => setIsLdReady(true))
        .catch(() => setIsLdReady(true))
    }
  }, [user, ldClient])

  if (!user || !graphqlClient || accounts.length === 0 || !isLdReady) return <LoadingCircular />

  async function logout() {
    await persistor?.purge()
    await graphqlClient?.clearStore()
    AzureInstance.logoutRedirect()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        logout,
        session,
      }}
    >
      <ApolloProvider client={graphqlClient}>
        <UserPreferencesProvider user={user} isAuthenticated={true}>
          {children}
        </UserPreferencesProvider>
      </ApolloProvider>
    </AuthContext.Provider>
  )
}

export default AuthProviderAzure
